import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, X, HelpCircle, Sparkles, Clock, ExternalLink, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_GROUPS, type FaqStakeholder } from "@/data/faqs";
import { cn } from "@/lib/utils";

interface Props {
  defaultGroup?: FaqStakeholder;
  className?: string;
}

export default function FaqSection({ defaultGroup = "buying", className }: Props) {
  const [active, setActive] = useState<FaqStakeholder>(defaultGroup);
  const group = useMemo(
    () => FAQ_GROUPS.find((g) => g.id === active) ?? FAQ_GROUPS[0],
    [active],
  );
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    if (!q) return group.items;
    const seen = new Set<string>();
    const words = q.split(/\s+/);
    return FAQ_GROUPS.flatMap((g) => g.items).filter((it) => {
      if (seen.has(it.q)) return false;
      const hay = [it.q, it.a, ...(it.bullets ?? []), it.note ?? ""].join(" ").toLowerCase();
      const hit = words.every((w) => hay.includes(w));
      if (hit) seen.add(it.q);
      return hit;
    });
  }, [q, group]);

  const jsonLd = useMemo(() => {
    const items = FAQ_GROUPS.flatMap((g) => g.items);
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
  }, []);

  return (
    <section
      id="faqs"
      className={cn(
        "relative mx-auto w-full max-w-4xl px-4 sm:px-6 py-16 sm:py-20",
        className,
      )}
      aria-labelledby="faqs-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8 sm:mb-10"
      >
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
          <HelpCircle className="h-3 w-3" /> FAQs
        </div>
        <h2
          id="faqs-heading"
          className="mt-3 font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight"
        >
          Questions, answered.
        </h2>
        <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          A growing library covering every part of the WCC360 journey. More for parents, schools and professionals on the way.
        </p>
      </motion.div>

      {/* Before-you-buy spotlight */}
      <div
        id="before-you-buy"
        className="mb-8 rounded-3xl border border-primary/40 bg-primary/10 p-5 sm:p-6"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
            <ShieldCheck className="h-3 w-3" aria-hidden="true" /> Before you buy
          </span>
        </div>
        <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold text-foreground">
          Deciding whether WCC360™ is worth it?
        </h3>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          Straight answers on what you get, how it differs from free AI tools, how long it takes, and how your data is protected.
        </p>
        <div className="mt-4 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={() => {
              document.getElementById("faq-buying")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Read the answers <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
          <Link
            to="/payment"
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-border/70 bg-card/60 px-4 text-sm font-semibold text-foreground hover:border-primary/50"
          >
            See what's included
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <label htmlFor="faq-search" className="sr-only">Search the FAQs</label>
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          id="faq-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions, e.g. refund, privacy, parents"
          className="h-12 w-full rounded-full border border-input bg-background pl-12 pr-12 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-1 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <p className="sr-only" aria-live="polite">{q ? `${shown.length} matching questions` : ""}</p>

      {!q && (
        <nav aria-label="FAQ topics" className="mb-10 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <span className="text-muted-foreground">Jump to:</span>
          {FAQ_GROUPS.filter((g) => g.items.length > 0).map((g) => (
            <a key={g.id} href={`#faq-${g.id}`} className="font-medium text-primary underline-offset-4 hover:underline">
              {g.label}
            </a>
          ))}
        </nav>
      )}
      {q && (
        <p className="mb-4 text-sm text-muted-foreground">
          {shown.length} {shown.length === 1 ? "result" : "results"} across all topics
        </p>
      )}

      {/* Content */}
      {q && shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <p className="font-display text-base font-semibold text-foreground">No questions match "{query.trim()}"</p>
          <p className="mt-1.5 text-sm text-muted-foreground">Try a different word, or write to us at hello@wekit.ai.</p>
        </div>
      ) : shown.length > 0 ? (
        <div id="faqs-list" className="space-y-12">
          {(q ? [{ id: "results", label: "", description: "", items: shown }] : FAQ_GROUPS.filter((g) => g.items.length > 0)).map((sec) => (
            <section key={sec.id} id={`faq-${sec.id}`} className="scroll-mt-24" aria-labelledby={sec.label ? `faq-h-${sec.id}` : undefined}>
              {sec.label && (
                <div className="mb-4">
                  <h2 id={`faq-h-${sec.id}`} className="font-display text-2xl font-semibold text-foreground">{sec.label}</h2>
                  {sec.description && <p className="mt-1 text-sm text-muted-foreground">{sec.description}</p>}
                </div>
              )}
            <Accordion
              
              type="single"
              collapsible
              className="faq-plus divide-y divide-border/60 border-y border-border/60"
            >
              {sec.items.map((it, i) => (
                <AccordionItem
                  key={i}
                  value={`${sec.id}-${i}`}
                  className="border-b-0 px-1 sm:px-2"
                >
                  <AccordionTrigger className="group justify-start gap-4 text-left text-base font-normal hover:no-underline py-4 data-[state=open]:font-semibold [&>svg]:hidden">
                    <span aria-hidden="true" className="relative h-4 w-4 shrink-0">
                      <span className="absolute left-0 top-1/2 h-0.5 w-4 -translate-y-1/2 bg-foreground" />
                      <span className="absolute left-1/2 top-0 h-4 w-0.5 -translate-x-1/2 bg-foreground transition-transform duration-200 motion-reduce:transition-none group-data-[state=open]:scale-y-0" />
                    </span>
                    <span className="flex-1">{it.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-8 text-base text-foreground/85 leading-relaxed pb-5 space-y-3 max-w-3xl">
                    {it.a.split("\n\n").map((para, k) => (
                      <p key={k}>{para}</p>
                    ))}
                    {it.bullets && it.bullets.length > 0 && (
                      <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 pl-4 list-disc marker:text-primary/60">
                        {it.bullets.map((b, j) => (
                          <li key={j} className="text-foreground/80">{b}</li>
                        ))}
                      </ul>
                    )}
                    {it.note && <p className="pt-1">{it.note}</p>}

                    {it.links && it.links.length > 0 && (
                      <div className="pt-1">
                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                          Sources
                        </p>
                        <ul className="mt-1.5 flex flex-col gap-1">
                          {it.links.map((l) =>
                            l.external ? (
                              <li key={l.href}>
                                <a
                                  href={l.href}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="inline-flex min-h-9 items-center gap-1.5 text-xs font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                                >
                                  {l.label}
                                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                                </a>
                              </li>
                            ) : (
                              <li key={l.href}>
                                <Link
                                  to={l.href}
                                  className="inline-flex min-h-9 items-center text-xs font-medium text-primary underline underline-offset-2"
                                >
                                  {l.label}
                                </Link>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            </section>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-primary/30 bg-card/30 p-8 text-center">
          <Sparkles className="h-5 w-5 mx-auto text-primary mb-2" />
          <p className="font-display text-base font-semibold text-foreground">
            Coming soon
          </p>
          <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
            We're curating a dedicated set of answers for {group.label.toLowerCase()}. Check back shortly.
          </p>
        </div>
      )}

      {/* Closing reassurance */}
      <div className="mt-8 rounded-3xl border border-border/60 bg-card/50 p-5 sm:p-6 text-center">
        <MessageCircle className="mx-auto h-5 w-5 text-primary" aria-hidden="true" />
        <p className="mt-2 font-display text-base sm:text-lg font-semibold text-foreground">
          Still have questions? We'd love to help.
        </p>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl mx-auto">
          Reach out to YoMA or the WeKIT team. We're here to help you make one of the most important decisions of your life with confidence.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2.5">
          <Link
            to="/start"
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground hover:scale-[1.02] transition-transform"
          >
            Start your journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a
            href="mailto:hello@wekit.ai"
            className="inline-flex min-h-11 items-center rounded-full border border-border/70 bg-card/60 px-4 text-sm font-semibold text-foreground hover:border-primary/50"
          >
            Talk to the WeKIT team
          </a>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Have a question we haven't answered?{" "}
        <a href="mailto:hello@wekit.ai" className="story-link text-foreground font-medium">
          Talk to us
        </a>
        .
      </p>
    </section>
  );
}
